'use client';

import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PersonIcon from '@mui/icons-material/Person';
import ViewContainer from '../layouts/ViewContainer';
import { useTelegram } from '@/providers/TelegramProvider';
import { useClipboard } from '@/hooks/useClipboard';
import { Card, CardContent, CircularProgress, Divider, Snackbar, Alert } from '@mui/material';
import env from '@/utils/config';

interface Referral {
    id: number;
    username: string | null;
    firstName: string | null;
    lastName: string | null;
    createdAt: string;
}

interface ReferralsViewProps {
    onBack: () => void;
}

export default function ReferralsView({ onBack }: ReferralsViewProps) {
    const { user } = useTelegram();
    const { copyToClipboard, copySuccess } = useClipboard();
    const [referrals, setReferrals] = React.useState<Referral[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [referralCode, setReferralCode] = React.useState<string | null>(null);
    const [snackbarOpen, setSnackbarOpen] = React.useState(false);

    React.useEffect(() => {
        const fetchReferrals = async () => {
            try {
                const response = await fetch('/referrals');
                const data = await response.json();
                if (data.success) {
                    setReferrals(data.referrals);
                    setReferralCode(data.referralCode);
                }
            } catch (error) {
                console.error('Failed to fetch referrals:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchReferrals();
    }, []);

    React.useEffect(() => {
        if (copySuccess) {
            setSnackbarOpen(true);
        }
    }, [copySuccess]);

    const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
    };

    const getReferralLink = () => {
        if (!referralCode) return '';
        // Формируем ссылку: t.me/botname?startapp=REFCODE
        return `${env.PUBLIC_TG_BOT_URL}?startapp=${referralCode}`;
    };

    const handleCopyLink = () => {
        const link = getReferralLink();
        if (link) {
            copyToClipboard(link);
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('ru-RU', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const getDisplayName = (ref: Referral) => {
        if (ref.username) return `@${ref.username}`;
        const nameParts = [ref.firstName, ref.lastName].filter(Boolean);
        return nameParts.length > 0 ? nameParts.join(' ') : `User #${ref.id}`;
    };

    return (
        <ViewContainer title="Реферальная программа" onClose={onBack}>
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                
                {/* Карточка с ссылкой */}
                <Card sx={{ 
                    bgcolor: 'rgba(255, 255, 255, 0.05)', 
                    backdropFilter: 'blur(10px)',
                    borderRadius: 2
                }}>
                    <CardContent>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Ваша реферальная ссылка
                        </Typography>
                        
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : (
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                bgcolor: 'rgba(0, 0, 0, 0.2)', 
                                p: 1.5, 
                                borderRadius: 1,
                                justifyContent: 'space-between'
                            }}>
                                <Typography variant="body2" sx={{ 
                                    overflow: 'hidden', 
                                    textOverflow: 'ellipsis', 
                                    whiteSpace: 'nowrap',
                                    mr: 1,
                                    fontFamily: 'monospace'
                                }}>
                                    {getReferralLink()}
                                </Typography>
                                <IconButton onClick={handleCopyLink} size="small" color="primary">
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        )}
                        
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            Приглашайте друзей и получайте бонусы!
                        </Typography>
                    </CardContent>
                </Card>

                <Divider sx={{ my: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                        ПРИГЛАШЕННЫЕ ПОЛЬЗОВАТЕЛИ ({referrals.length})
                    </Typography>
                </Divider>

                {/* Список рефералов */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : referrals.length > 0 ? (
                    <List sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: 2 }}>
                        {referrals.map((ref, index) => (
                            <React.Fragment key={ref.id}>
                                <ListItem alignItems="flex-start">
                                    <ListItemAvatar>
                                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                                            {ref.firstName ? ref.firstName[0].toUpperCase() : <PersonIcon />}
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={getDisplayName(ref)}
                                        secondary={
                                            <Typography
                                                component="span"
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                Регистрация: {formatDate(ref.createdAt)}
                                            </Typography>
                                        }
                                    />
                                </ListItem>
                                {index < referrals.length - 1 && <Divider variant="inset" component="li" />}
                            </React.Fragment>
                        ))}
                    </List>
                ) : (
                    <Box sx={{ textAlign: 'center', py: 4, opacity: 0.7 }}>
                        <PersonIcon sx={{ fontSize: 48, mb: 1, color: 'text.secondary' }} />
                        <Typography variant="body1" color="text.secondary">
                            У вас пока нет рефералов
                        </Typography>
                    </Box>
                )}
            </Box>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={2000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
                    Ссылка скопирована!
                </Alert>
            </Snackbar>
        </ViewContainer>
    );
}
